/**
 * Customer languages in the edit drawer.
 *
 * The API stores languages as `{ language: { id }, default }` links. The first language the
 * planner picks is the default (the one used in e-mails). The select has no separate "default"
 * control, so that language is labelled with `[default]` in its tag.
 */

export const DEFAULT_LANGUAGE_TAG = "[default]";

const DEFAULT_LANGUAGE_TAG_PATTERN = /\s*\[default\]\s*$/i;

export type CustomerLanguageLink = {
  id: string,
  language: {
    id: string,
    label?: string,
    isoCode?: string
  },
  default?: boolean,
  isDefault?: boolean
};

export function stripDefaultLanguageTag(label: string): string {
  return label.replace(DEFAULT_LANGUAGE_TAG_PATTERN, "").trim();
}

export function withDefaultLanguageTag(label: string, isDefault: boolean): string {
  const base = stripDefaultLanguageTag(label);
  return isDefault ? `${base} ${DEFAULT_LANGUAGE_TAG}` : base;
}

export function isCustomerLanguageLink(value: unknown): value is CustomerLanguageLink {
  if (!value || typeof value !== "object") return false;
  const link = value as { language?: { id?: unknown } };
  return typeof link.language?.id === "string";
}

export function isLanguageRecord(value: unknown): value is EasyTech.Language {
  if (!value || typeof value !== "object") return false;
  const language = value as { id?: unknown, label?: unknown };
  return typeof language.id === "string" && typeof language.label === "string";
}

/**
 * First selected language is the customer's default. Existing link ids are reused so a save
 * updates the same rows instead of recreating them.
 * Both `default` and `isDefault` flags are set to ensure full interoperability with core and web-app.
 */
export function toCustomerLanguagePayload(
  languages: EasyTech.Language[],
  existing: unknown[] = []
): CustomerLanguageLink[] {
  const existingIds = new Map<string, string>();

  existing.forEach((item) => {
    if (isCustomerLanguageLink(item)) {
      existingIds.set(item.language.id, item.id);
    }
  });

  return languages
    .filter((language) => language?.id)
    .map((language, index) => {
      const isDefault = index === 0;
      return {
        id: existingIds.get(language.id as string) || crypto.randomUUID(),
        language: { id: language.id as string },
        default: isDefault,
        isDefault: isDefault
      };
    });
}

/** `[default]` on the customer's default language — not the platform-wide Language.default flag. */
export function markDefaultLanguageLabels(
  languages: EasyTech.Language[],
  defaultLanguageId?: string
): EasyTech.Language[] {
  const defaultId = defaultLanguageId ?? languages[0]?.id;

  return languages.map((language) => ({
    ...language,
    label: withDefaultLanguageTag(language.label, language.id === defaultId)
  }));
}

/**
 * Rebuild the select values from catalog languages, keeping the customer's order with the
 * default first. Inspects both `isDefault` and `default` to handle payloads from either core or client.
 */
export function resolveCustomerLanguagesForDisplay(
  catalog: EasyTech.Language[],
  links: CustomerLanguageLink[]
): EasyTech.Language[] {
  const validLinks = links.filter((link) => link.language?.id);
  const defaultLink = validLinks.find((link) => link.isDefault === true || link.default === true);
  const defaultId = defaultLink?.language.id ?? validLinks[0]?.language.id;
  const orderedIds = [
    ...(defaultId ? [defaultId] : []),
    ...validLinks.map((link) => link.language.id).filter((id) => id !== defaultId)
  ];

  const resolved = orderedIds
    .map((id) => {
      const foundInCatalog = catalog.find((language) => language.id === id);
      if (foundInCatalog) return foundInCatalog;
      const link = validLinks.find((l) => l.language.id === id);
      if (link?.language && typeof (link.language as any).label === "string") {
        return {
          id: link.language.id,
          label: (link.language as any).label,
          isoCode: (link.language as any).isoCode || link.language.id,
          default: false
        } as EasyTech.Language;
      }
      return undefined;
    })
    .filter((language): language is EasyTech.Language => !!language);

  return markDefaultLanguageLabels(resolved, defaultId);
}
